import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TickerAutocomplete from "@/components/TickerAutocomplete";
import ResearchChatCard from "@/components/ResearchChatCard";
import TickerTape from "@/components/TickerTape";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
  BarChart, Bar,
} from "recharts";
import {
  Compass, Loader2, ExternalLink, Sparkles, ShieldCheck, TrendingUp, TrendingDown,
  Calendar as CalendarIcon, Newspaper, Copy, Building2, Info, PlusCircle, Star, GitCompareArrows,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import usePageSeo from "@/hooks/usePageSeo";

const RANGES = ["1M", "3M", "6M", "1Y", "3Y", "5Y", "10Y", "MAX"];

const fmt$ = (v, digits = 2) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const fmtNum = (v) => v == null ? "—" : Number(v).toLocaleString();
const fmtPct = (v, digits = 2) => v == null ? "—" : `${Number(v).toFixed(digits)}%`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso || "—"; } };
const fmtMktCap = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9)  return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toLocaleString()}`;
};

export default function Research() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialT = (params.get("t") || "").toUpperCase();
  const initialB = (params.get("b") || "").toUpperCase();
  usePageSeo({
    title: initialT ? `${initialT} · Research — Dividend Portfolio Planner` : "Ticker Research — Dividend Portfolio Planner",
    description: initialT
      ? `Deep dive on ${initialT} — dividend safety, payout ratio, 62-year history, ex-div calendar, and an AI-written thesis.`
      : "Research 8,000+ dividend tickers. Safety score, payout ratio, 62-year history, ex-div calendar, and AI thesis.",
    path: initialT ? `/research?t=${initialT}` : "/research",
  });
  const [query, setQuery] = useState(initialT);
  const [ticker, setTicker] = useState(initialT || null);
  const [compareB, setCompareB] = useState(initialB || null);  // when set, render CompareView
  const [themes, setThemes] = useState([]);
  const [themeKey, setThemeKey] = useState("");
  const [themeTickers, setThemeTickers] = useState([]);
  const [watchlist, setWatchlist] = useState(new Set());

  const loadWatchlist = useCallback(async () => {
    // Anonymous visitors can't have a watchlist — skip the call entirely so
    // we don't spam the console with 401s that make Research look broken.
    if (!user) return;
    try {
      const r = await api.get("/watchlist");
      setWatchlist(new Set((r.data.items || []).map((i) => i.ticker)));
    } catch { /* not signed in? */ }
  }, [user]);

  useEffect(() => {
    api.get("/research/themes").then((r) => setThemes(r.data.themes || [])).catch(() => {});
    loadWatchlist();
  }, [loadWatchlist]);

  // Guard the toggle-star action too — nudge anonymous users to sign in
  // instead of silently 401-ing when they try to star a ticker.
  const requireAuthForWatchlist = () => {
    if (!user) {
      toast.info("Sign in (free) to save tickers to your watchlist");
      return true;
    }
    return false;
  };

  const openTheme = async (key) => {
    setThemeKey(key);
    try {
      const r = await api.get(`/research/themes/${encodeURIComponent(key)}`);
      setThemeTickers(r.data.tickers || []);
    } catch { setThemeTickers([]); }
  };

  const openTicker = useCallback((t, opts = {}) => {
    const T = (t || "").trim().toUpperCase();
    if (!T) return;
    setTicker(T);
    setQuery(T);
    if (opts.clearCompare) setCompareB(null);
    setParams(compareB && !opts.clearCompare ? { t: T, b: compareB } : { t: T }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setParams, compareB]);

  const startCompare = (b) => {
    const B = (b || "").trim().toUpperCase();
    if (!ticker || !B || B === ticker) return;
    setCompareB(B);
    setParams({ t: ticker, b: B }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const exitCompare = () => {
    setCompareB(null);
    setParams({ t: ticker }, { replace: true });
  };

  const toggleStar = async (t) => {
    if (requireAuthForWatchlist()) return;
    const T = (t || "").toUpperCase();
    const isStarred = watchlist.has(T);
    try {
      if (isStarred) {
        await api.delete(`/watchlist/${T}`);
        setWatchlist((prev) => { const n = new Set(prev); n.delete(T); return n; });
        toast.success(`Removed ${T} from watchlist`);
      } else {
        await api.post(`/watchlist/${T}`);
        setWatchlist((prev) => new Set(prev).add(T));
        toast.success(`Added ${T} to watchlist`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Watchlist update failed");
    }
  };

  return (
    <>
      <TickerTape />
      <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Ticker Research</div>
          <h1 className="serif text-4xl sm:text-5xl mt-1">Look before you buy.</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Company snapshot, price chart, dividend safety score, payout history, news, and an AI-written thesis — for any US-listed dividend stock or ETF.
          </p>
        </div>
      </div>

      {/* Search + Themes + AI Chat (chat card is logged-in only) */}
      <div className={`mb-6 grid gap-6 items-stretch ${user ? "lg:grid-cols-2" : ""}`}>
        <Card>
          <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-end">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Search by ticker</div>
              <TickerAutocomplete
                testId="research-search"
                value={query}
                onChange={setQuery}
                onSelect={(row) => openTicker(row.ticker)}
                placeholder="e.g. SCHD, JEPI, O, KO"
              />
            </div>
            <Button
              onClick={() => openTicker(query)}
              disabled={!query.trim()}
              data-testid="research-go"
              className="rounded-full"
            >
              Research <Compass className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Or browse a theme</div>
            <div className="flex flex-wrap gap-2" data-testid="research-themes">
              {themes.map((t) => (
                <button
                  key={t.key}
                  onClick={() => openTheme(t.key)}
                  data-testid={`theme-${t.key}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    themeKey === t.key
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border hover:border-primary/40"
                  }`}
                >
                  <span>{t.label}</span>
                  <span className="mono text-[10px] opacity-70">{t.count}</span>
                </button>
              ))}
            </div>
            {themeTickers.length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="theme-tickers">
                {themeTickers.map((r) => (
                  <button
                    key={r.ticker}
                    onClick={() => openTicker(r.ticker)}
                    data-testid={`theme-ticker-${r.ticker}`}
                    className="text-left flex items-baseline justify-between gap-3 rounded-md border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="mono text-sm font-semibold tracking-wider">{r.ticker}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.name}</div>
                    </div>
                    <span className="mono text-[9px] uppercase tracking-widest text-secondary shrink-0">{r.category}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
        {user && <ResearchChatCard />}
      </div>

      {compareB && ticker ? (
        <CompareView
          a={ticker}
          b={compareB}
          watchlist={watchlist}
          toggleStar={toggleStar}
          onExit={exitCompare}
        />
      ) : ticker ? (
        <ResearchDetail
          ticker={ticker}
          starred={watchlist.has(ticker)}
          toggleStar={() => toggleStar(ticker)}
          startCompare={startCompare}
        />
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-14 text-center">
            <div className="w-14 h-14 rounded-full bg-secondary/10 text-secondary grid place-items-center mx-auto">
              <Compass className="w-6 h-6" />
            </div>
            <h2 className="serif text-3xl mt-4">Pick a ticker to begin</h2>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Try <button className="underline underline-offset-2 hover:text-primary mono" onClick={() => openTicker("SCHD")}>SCHD</button>,{" "}
              <button className="underline underline-offset-2 hover:text-primary mono" onClick={() => openTicker("O")}>O</button>,{" "}
              <button className="underline underline-offset-2 hover:text-primary mono" onClick={() => openTicker("JEPI")}>JEPI</button>,{" "}
              or browse a theme above.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
    </>
  );
}

// ---------- Detail view ----------

function ResearchDetail({ ticker, starred, toggleStar, startCompare }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [range, setRange] = useState("1Y");
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [thesis, setThesis] = useState(null);
  const [thesisLoading, setThesisLoading] = useState(false);

  // Load profile whenever ticker changes.
  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(""); setProfile(null); setThesis(null);
    api.get(`/research/${encodeURIComponent(ticker)}`)
      .then((r) => { if (alive) setProfile(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load ticker"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ticker]);

  // Load price series when range or ticker changes.
  useEffect(() => {
    let alive = true;
    setPriceLoading(true);
    api.get(`/research/${encodeURIComponent(ticker)}/prices`, { params: { range } })
      .then((r) => { if (alive) setPriceData(r.data); })
      .catch(() => { if (alive) setPriceData(null); })
      .finally(() => { if (alive) setPriceLoading(false); });
    return () => { alive = false; };
  }, [ticker, range]);

  const runThesis = async () => {
    if (!profile) return;
    setThesisLoading(true);
    try {
      const r = await api.post(`/research/${encodeURIComponent(ticker)}/thesis`, {
        context: {
          snapshot: profile.snapshot,
          performance: profile.performance,
          safety: profile.safety,
          dividends: profile.dividends,
        },
      });
      setThesis(r.data.thesis);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Thesis failed";
      toast.error(detail);
    } finally { setThesisLoading(false); }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="mt-3 text-muted-foreground text-sm">Loading {ticker} research…</p>
      </CardContent></Card>
    );
  }
  if (err || !profile) {
    return (
      <Card><CardContent className="p-10 text-center">
        <div className="serif text-2xl">Nothing here for <span className="mono">{ticker}</span></div>
        <p className="text-muted-foreground text-sm mt-2">{err || "yfinance returned no data for this symbol."}</p>
      </CardContent></Card>
    );
  }

  const s = profile.snapshot;
  return (
    <div className="space-y-6" data-testid="research-detail">
      {/* Header + snapshot */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="serif text-4xl">{s.symbol || ticker}</span>
                <span className="serif text-2xl text-muted-foreground truncate">{s.name}</span>
                {s.quote_type && (
                  <Badge variant="outline" className="mono text-[10px] uppercase">{s.quote_type}</Badge>
                )}
              </div>
              <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
                {[s.sector, s.industry, s.country].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="serif text-4xl text-primary">{fmt$(s.price)}</div>
              <div className="mono text-[10px] text-muted-foreground">{s.currency || "USD"}</div>
              {s.website && (
                <a href={s.website} target="_blank" rel="noopener noreferrer" className="mono text-[10px] uppercase tracking-widest text-secondary hover:underline inline-flex items-center gap-1 mt-1">
                  Website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          {/* Action toolbar */}
          <div className="mt-4 flex flex-wrap gap-2" data-testid="research-actions">
            <Button
              onClick={() => nav(`/portfolio/new?seed=${encodeURIComponent(ticker)}`)}
              className="rounded-full"
              data-testid="research-add-to-portfolio"
            >
              <PlusCircle className="w-4 h-4 mr-2" /> Add to new portfolio
            </Button>
            <Button
              variant={starred ? "default" : "outline"}
              onClick={toggleStar}
              className={`rounded-full ${starred ? "bg-secondary text-secondary-foreground hover:bg-secondary/90" : ""}`}
              data-testid="research-star"
              aria-pressed={starred}
            >
              <Star className={`w-4 h-4 mr-2 ${starred ? "fill-current" : ""}`} />
              {starred ? "Watchlisted" : "Add to watchlist"}
            </Button>
            <CompareWithButton ticker={ticker} onCompare={startCompare} />
          </div>

          {/* Key metrics strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-6">
            <KV label="Yield" value={fmtPct(s.dividend_yield_pct)} accent="secondary" />
            <KV label="Payout ratio" value={fmtPct(s.payout_ratio_pct)} />
            <KV label="Div. rate" value={fmt$(s.dividend_rate_annual)} />
            <KV label="Growth streak" value={`${profile.dividends.growth_streak_years} yr`} accent="primary" />
            <KV label="Market cap" value={fmtMktCap(s.market_cap)} />
            <KV label="Trailing P/E" value={s.trailing_pe == null ? "—" : Number(s.trailing_pe).toFixed(1)} />
          </div>

          {s.description && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-secondary hover:underline flex items-center gap-2">
                <Info className="w-4 h-4" /> Business summary
              </summary>
              <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{s.description}</p>
            </details>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="chart">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="chart" data-testid="tab-chart">Price chart</TabsTrigger>
          <TabsTrigger value="fundamentals" data-testid="tab-fundamentals">Performance & Risk</TabsTrigger>
          <TabsTrigger value="dividends" data-testid="tab-dividends">Dividends</TabsTrigger>
          <TabsTrigger value="safety" data-testid="tab-safety">Safety score</TabsTrigger>
          <TabsTrigger value="news" data-testid="tab-news">News</TabsTrigger>
          <TabsTrigger value="thesis" data-testid="tab-thesis">AI Thesis</TabsTrigger>
        </TabsList>

        {/* Chart */}
        <TabsContent value="chart" className="mt-4">
          <Card><CardContent className="p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="serif text-2xl">Closing price · {range}</h3>
              <div className="flex flex-wrap gap-1" data-testid="range-selector">
                {RANGES.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    data-testid={`range-${r}`}
                    className={`mono text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      r === range ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"
                    }`}
                  >{r}</button>
                ))}
              </div>
            </div>
            {priceLoading || !priceData ? (
              <div className="h-72 grid place-items-center text-sm text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <KV label="Start" value={fmt$(priceData.start_close)} />
                  <KV label="End" value={fmt$(priceData.end_close)} accent="primary" />
                  <KV
                    label="Change"
                    value={priceData.change_pct == null ? "—" : `${priceData.change_pct >= 0 ? "+" : ""}${priceData.change_pct.toFixed(2)}%`}
                    accent={priceData.change_pct >= 0 ? "primary" : "destructive"}
                  />
                  <KV label="Range" value={`${fmt$(priceData.low)} – ${fmt$(priceData.high)}`} />
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={priceData.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={30} />
                      <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10 }} width={55} />
                      <Tooltip
                        contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
                        formatter={(v) => [fmt$(v), "Close"]}
                      />
                      <Line type="monotone" dataKey="close" stroke="#0F3D32" strokeWidth={2} dot={false} />
                      <ReferenceLine y={priceData.start_close} stroke="#8B7355" strokeDasharray="4 4" opacity={0.5} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Performance */}
        <TabsContent value="fundamentals" className="mt-4">
          <PerformanceCard perf={profile.performance} snapshot={s} />
        </TabsContent>

        {/* Dividends */}
        <TabsContent value="dividends" className="mt-4">
          <DividendsCard dividends={profile.dividends} payoutHistory={profile.payout_ratio_history} snapshot={s} />
        </TabsContent>

        {/* Safety */}
        <TabsContent value="safety" className="mt-4">
          <SafetyCard safety={profile.safety} />
        </TabsContent>

        {/* News */}
        <TabsContent value="news" className="mt-4">
          <Card><CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Newspaper className="w-4 h-4 text-secondary" />
              <h3 className="serif text-2xl">Recent news</h3>
            </div>
            {profile.news.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">No recent news from Yahoo Finance for this ticker.</div>
            ) : (
              <div className="space-y-3" data-testid="news-list">
                {profile.news.map((n, i) => (
                  <a
                    key={i}
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-md border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors px-4 py-3"
                    data-testid={`news-item-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="serif text-base leading-snug">{n.title}</div>
                        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                          {[n.publisher, fmtDate(n.published_at)].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-secondary shrink-0" />
                    </div>
                  </a>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* AI Thesis */}
        <TabsContent value="thesis" className="mt-4">
          <Card><CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> AI thesis · Claude Sonnet 4.6
                </div>
                <h3 className="serif text-2xl mt-2">A plain-English take on {ticker}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-lg">
                  Balanced 220-word analysis grounded in the numbers on this page. Counts against your monthly AI budget.
                </p>
              </div>
              <Button onClick={runThesis} disabled={thesisLoading} className="rounded-full" data-testid="thesis-run">
                {thesisLoading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Thinking…</>
                  : thesis ? "Regenerate" : "Write thesis"}
              </Button>
            </div>
            {thesis && (
              <div className="rounded-md border border-secondary/20 bg-secondary/[0.03] p-4">
                <div className="flex items-center justify-end mb-1">
                  <button
                    onClick={() => { navigator.clipboard?.writeText(thesis); toast.success("Copied"); }}
                    className="text-muted-foreground hover:text-primary transition-colors"
                    aria-label="Copy"
                    data-testid="thesis-copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="thesis-text">{thesis}</div>
              </div>
            )}
            {!thesis && !thesisLoading && (
              <div className="text-sm text-muted-foreground italic">
                Click <span className="mono">Write thesis</span> for a durable-dividend take with risks and a takeaway.
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <p className="text-[10px] text-muted-foreground text-center mt-6">
        Data: Yahoo Finance (yfinance). Educational tool — not investment advice.
      </p>
    </div>
  );
}

// ---------- Sub-cards ----------

function KV({ label, value, accent, testId }) {
  const cls = accent === "primary" ? "text-primary" :
              accent === "secondary" ? "text-secondary" :
              accent === "destructive" ? "text-destructive" : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2" data-testid={testId}>
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`serif text-lg mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function PerformanceCard({ perf, snapshot }) {
  const empty = !perf || Object.keys(perf).length === 0;
  const positive = Number(perf?.annualized_return_pct) >= 0;
  return (
    <Card><CardContent className="p-6">
      <div className="flex items-center gap-2 mb-4">
        {positive ? <TrendingUp className="w-4 h-4 text-primary" /> : <TrendingDown className="w-4 h-4 text-destructive" />}
        <h3 className="serif text-2xl">Performance & risk (5-year)</h3>
      </div>
      {empty ? (
        <div className="text-sm text-muted-foreground italic">Not enough history to compute risk metrics.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <KV label="Ann. return" value={fmtPct(perf.annualized_return_pct)} accent={positive ? "primary" : "destructive"} testId="perf-annret" />
          <KV label="Ann. volatility" value={fmtPct(perf.annualized_volatility_pct)} />
          <KV label="Max drawdown" value={fmtPct(perf.max_drawdown_pct)} accent="destructive" />
          <KV label="Sharpe (rf 4.5%)" value={perf.sharpe_ratio_rf4_5 == null ? "—" : Number(perf.sharpe_ratio_rf4_5).toFixed(2)} />
          <KV label="Beta" value={perf.beta == null ? "—" : Number(perf.beta).toFixed(2)} />
          <KV label="52w high" value={fmt$(perf.week52_high)} />
          <KV label="52w low" value={fmt$(perf.week52_low)} />
          <KV label="Current price" value={fmt$(snapshot?.price)} accent="primary" />
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-4">
        Metrics computed from daily returns over the last 5 years. Sharpe uses a 4.5% risk-free rate.
      </p>
    </CardContent></Card>
  );
}

function DividendsCard({ dividends, payoutHistory, snapshot }) {
  const recent = dividends?.history || [];
  const nextExDiv = dividends?.next_ex_dividend_date;
  const streak = dividends?.growth_streak_years || 0;
  // Group last 5 years of dividends by year for the mini bar chart.
  const byYear = useMemo(() => {
    const m = new Map();
    for (const r of recent) {
      const y = Number(String(r.date).slice(0, 4));
      if (!Number.isFinite(y)) continue;
      m.set(y, (m.get(y) || 0) + Number(r.amount));
    }
    return [...m.entries()]
      .sort((a, b) => a[0] - b[0])
      .slice(-5)
      .map(([year, amount]) => ({ year: String(year), amount: Number(amount.toFixed(4)) }));
  }, [recent]);
  return (
    <div className="space-y-6">
      <Card><CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="w-4 h-4 text-secondary" />
          <h3 className="serif text-2xl">Dividend calendar</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KV label="Trailing yield" value={fmtPct(snapshot?.dividend_yield_pct)} accent="secondary" />
          <KV label="Annual rate" value={fmt$(snapshot?.dividend_rate_annual)} />
          <KV label="Next ex-div date" value={nextExDiv ? fmtDate(nextExDiv) : "—"} accent="primary" testId="next-ex-div" />
          <KV label="Growth streak" value={`${streak} yr${streak === 1 ? "" : "s"}`} />
        </div>

        {byYear.length > 0 && (
          <div className="mt-6">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Total dividends per year (last 5)</div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byYear} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={45} />
                  <Tooltip
                    contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
                    formatter={(v) => [fmt$(v, 4), "Per share"]}
                  />
                  <Bar dataKey="amount" fill="#8B7355" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="serif text-2xl">Recent dividend payments</h3>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No dividend history from Yahoo Finance.</div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-left">
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Ex-date</th>
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Amount / share</th>
                </tr>
              </thead>
              <tbody data-testid="dividend-history">
                {recent.slice(0, 16).map((r) => (
                  <tr key={r.date} className="border-t border-border">
                    <td className="px-3 py-1.5 mono text-xs">{fmtDate(r.date)}</td>
                    <td className="px-3 py-1.5 mono text-xs text-right">{fmt$(r.amount, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      {payoutHistory?.length > 0 && (
        <Card><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-secondary" />
            <h3 className="serif text-2xl">Payout ratio history</h3>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={payoutHistory} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={45} unit="%" />
                <Tooltip
                  contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => [`${v}%`, "Payout"]}
                />
                <Bar dataKey="payout_ratio_pct" fill="#0F3D32" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Dividends paid divided by net income, from annual financials. Higher and rising = less cushion for future increases.
          </p>
        </CardContent></Card>
      )}
    </div>
  );
}

function SafetyCard({ safety }) {
  const s = safety?.score;
  const bar = (score) => (
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div className="h-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, Number(score) || 0))}%` }} />
    </div>
  );
  const gradeColor = s == null ? "text-muted-foreground" :
                     s >= 85 ? "text-primary" : s >= 65 ? "text-secondary" : "text-destructive";
  return (
    <Card><CardContent className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <h3 className="serif text-2xl">Dividend safety score</h3>
      </div>
      {s == null ? (
        <div className="text-sm text-muted-foreground italic">Not enough data (this is common for ETFs).</div>
      ) : (
        <>
          <div className="flex items-end gap-6 flex-wrap" data-testid="safety-summary">
            <div>
              <div className={`serif text-6xl ${gradeColor}`}>{safety.grade}</div>
              <div className="mono text-xs text-muted-foreground mt-1">Grade</div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="serif text-3xl">{s.toFixed(1)}<span className="text-muted-foreground text-lg">/100</span></div>
              <div className="mt-2">{bar(s)}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Composite score</div>
            </div>
          </div>
          <div className="mt-6 space-y-3" data-testid="safety-components">
            {safety.components.map((c) => (
              <div key={c.key} className="rounded-md border border-border px-3 py-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="mono text-xs uppercase tracking-widest text-muted-foreground">{c.key.replaceAll("_", " ")}</div>
                  <div className="flex items-center gap-3">
                    <span className="mono text-xs">{c.value}</span>
                    <span className="mono text-xs text-secondary">{c.score}/100</span>
                  </div>
                </div>
                <div className="mt-2">{bar(c.score)}</div>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="text-[11px] text-muted-foreground mt-4">
        Composite score of payout ratio, dividend growth streak, trailing yield reasonability, and debt-to-equity when available.
        Educational heuristic — not a substitute for full due diligence.
      </p>
    </CardContent></Card>
  );
}

// ---------- Compare-with ticker picker ----------

function CompareWithButton({ ticker, onCompare }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = React.useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={boxRef} className="relative">
      <Button
        variant="outline"
        className="rounded-full"
        onClick={() => setOpen((o) => !o)}
        data-testid="research-compare-open"
      >
        <GitCompareArrows className="w-4 h-4 mr-2" /> Compare with…
      </Button>
      {open && (
        <div className="absolute z-40 mt-2 right-0 w-72 rounded-md border border-border bg-background shadow-lg p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Compare {ticker} with
          </div>
          <TickerAutocomplete
            testId="compare-with-input"
            value={q}
            onChange={setQ}
            onSelect={(row) => { setOpen(false); setQ(""); onCompare(row.ticker); }}
            placeholder="Ticker to compare (e.g. VYM)"
            autoFocus
          />
        </div>
      )}
    </div>
  );
}

// ---------- Side-by-side compare view ----------

function CompareView({ a, b, watchlist, toggleStar, onExit }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    api.get(`/research/compare`, { params: { a, b } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Compare failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [a, b]);

  if (loading) {
    return (
      <Card><CardContent className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="mt-3 text-muted-foreground text-sm">Comparing {a} vs {b}…</p>
      </CardContent></Card>
    );
  }
  if (err || !data) {
    return (
      <Card><CardContent className="p-10 text-center">
        <div className="serif text-2xl">Couldn&apos;t compare <span className="mono">{a}</span> vs <span className="mono">{b}</span></div>
        <p className="text-muted-foreground text-sm mt-2">{err}</p>
        <Button variant="outline" className="rounded-full mt-4" onClick={onExit}>Back to {a}</Button>
      </CardContent></Card>
    );
  }
  const sa = data.a.snapshot, sb = data.b.snapshot;
  const pa = data.a.performance || {}, pb = data.b.performance || {};
  const dva = data.a.dividends || {}, dvb = data.b.dividends || {};
  const safea = data.a.safety || {}, safeb = data.b.safety || {};

  const rows = [
    { label: "Yield", a: fmtPct(sa.dividend_yield_pct), b: fmtPct(sb.dividend_yield_pct) },
    { label: "Payout ratio", a: fmtPct(sa.payout_ratio_pct), b: fmtPct(sb.payout_ratio_pct) },
    { label: "Div. rate", a: fmt$(sa.dividend_rate_annual), b: fmt$(sb.dividend_rate_annual) },
    { label: "Growth streak", a: `${dva.growth_streak_years || 0} yr`, b: `${dvb.growth_streak_years || 0} yr` },
    { label: "Safety grade", a: safea.grade || "—", b: safeb.grade || "—", highlight: true },
    { label: "Safety score", a: safea.score == null ? "—" : `${safea.score}/100`, b: safeb.score == null ? "—" : `${safeb.score}/100` },
    { label: "Ann. return (5y)", a: fmtPct(pa.annualized_return_pct), b: fmtPct(pb.annualized_return_pct) },
    { label: "Ann. volatility", a: fmtPct(pa.annualized_volatility_pct), b: fmtPct(pb.annualized_volatility_pct) },
    { label: "Max drawdown", a: fmtPct(pa.max_drawdown_pct), b: fmtPct(pb.max_drawdown_pct) },
    { label: "Sharpe (rf 4.5%)", a: pa.sharpe_ratio_rf4_5 == null ? "—" : Number(pa.sharpe_ratio_rf4_5).toFixed(2), b: pb.sharpe_ratio_rf4_5 == null ? "—" : Number(pb.sharpe_ratio_rf4_5).toFixed(2) },
    { label: "Market cap", a: fmtMktCap(sa.market_cap), b: fmtMktCap(sb.market_cap) },
    { label: "Sector", a: sa.sector || "—", b: sb.sector || "—" },
  ];

  return (
    <div className="space-y-6" data-testid="compare-view">
      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <GitCompareArrows className="w-5 h-5 text-secondary" />
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-secondary">Side-by-side comparison</div>
              <h2 className="serif text-3xl mt-1">
                <span className="mono">{a}</span> <span className="text-muted-foreground">vs</span> <span className="mono">{b}</span>
              </h2>
            </div>
          </div>
          <Button variant="outline" className="rounded-full" onClick={onExit} data-testid="compare-exit">
            Exit compare
          </Button>
        </div>

        {/* Header row: name + price + star */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {[{ tk: a, s: sa }, { tk: b, s: sb }].map(({ tk, s }) => (
            <div key={tk} className="rounded-md border border-border bg-card px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="serif text-2xl">{tk}</div>
                  <div className="text-sm text-muted-foreground truncate">{s.name}</div>
                </div>
                <button
                  onClick={() => toggleStar(tk)}
                  className={watchlist.has(tk) ? "text-secondary" : "text-muted-foreground hover:text-secondary"}
                  data-testid={`compare-star-${tk}`}
                  aria-label="Star"
                >
                  <Star className={`w-5 h-5 ${watchlist.has(tk) ? "fill-current" : ""}`} />
                </button>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <div className="serif text-2xl text-primary">{fmt$(s.price)}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.quote_type || "—"}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Normalized chart */}
      {data.chart && (
        <Card><CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="serif text-2xl">1-year performance (rebased to 100)</h3>
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {data.chart.series.length} points
            </span>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.chart.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={30} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10 }} width={45} />
                <Tooltip
                  contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => [Number(v).toFixed(2), ""]}
                />
                <Line type="monotone" dataKey={a} stroke="#0F3D32" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey={b} stroke="#E3A335" strokeWidth={2} dot={false} />
                <ReferenceLine y={100} stroke="#8B7355" strokeDasharray="4 4" opacity={0.4} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center gap-4 mono text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary" /> {a}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-secondary" /> {b}</span>
            <span className="text-muted-foreground">Higher line = better 1Y return</span>
          </div>
        </CardContent></Card>
      )}

      {/* Metrics diff table */}
      <Card><CardContent className="p-6">
        <h3 className="serif text-2xl mb-4">Metrics</h3>
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-muted/60">
              <tr>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-left">Metric</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">{a}</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">{b}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className={`border-t border-border ${r.highlight ? "bg-primary/[0.03]" : ""}`}>
                  <td className="px-3 py-2">{r.label}</td>
                  <td className="px-3 py-2 mono text-xs text-right">{r.a}</td>
                  <td className="px-3 py-2 mono text-xs text-right">{r.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

